import { memo as Memo, useState, useRef, useMemo, useEffect } from 'react';
import { useRouter } from 'next/router';

//* GSAP
import gsap from 'gsap';

//* HOC
import withUIContext from 'context/consumerHOC/UIConsumer';

//* Helper
import { useActiveSection } from 'helpers';

//* Components
import Text from 'components/common/Text';
import Button from 'components/common/Button';
import Container from 'components/common/Container';
import NewsAndEventsCard from 'components/common/NewsAndEventsCard';

//* Style
import LatestNewsStyle from './style';

const LatestNews = Memo(({ title, data, winWidth, className, isLoadMore = false, setEventOrNews, eventOrNews }) => {
	//! Router
	const router = useRouter();

	//! Refs
	const contRef = useRef();
	const newsItemRefs = useRef([]);
	const newsItemImageRefs = useRef([]);
	const itemsPerPage = useRef(3);

	//! States
	const [quantity, setQuantity] = useState(2);
	const activeSection = useActiveSection(contRef, 75);

	//!  News items
	const newsItems = useMemo(() => {
		return data.map((item, index) => {
			if (index < quantity * itemsPerPage.current) {
				return (
					<div
						key={index}
						className={`news-event-item col-4 col-ts-6 col-m-12`}>
						<NewsAndEventsCard
							image={item.featured_image}
							name={item.title}
							readTime={item.read_time}
							date={item.date}
							url={`/news-and-events/${item.slug}`}
							ref={(ref) => {
								newsItemRefs.current[index] = ref?.contRef;
								newsItemImageRefs.current[index] = ref?.imageRef;
							}}
						/>
					</div>
				);
			}
		});
	}, [data, winWidth, quantity, itemsPerPage.current, newsItemRefs.current, newsItemImageRefs.current]);

	useEffect(() => {
		if (eventOrNews !== 'event') {
			setEventOrNews(data[0].type);
		}
	}, [eventOrNews, data]);

	//! Animation
	useEffect(() => {
		const arr1 = [...newsItemRefs.current];
		const arr2 = [...newsItemImageRefs.current];

		//! Array for animations
		const arr1ForAnim = quantity !== 2 ? arr1.slice(-itemsPerPage.current) : arr1;
		const arr2ForAnim = quantity !== 2 ? arr2.slice(-itemsPerPage.current) : arr2;

		gsap.killTweensOf([arr1ForAnim, arr2ForAnim]);

		if (activeSection) {
			gsap.to(arr1ForAnim, { duration: 1, stagger: 0.1, y: '0%', ease: 'power2.out' });
			gsap.to(arr2ForAnim, { delay: 0.2, stagger: 0.1, duration: 0.7, borderRadius: activeSection ? `${winWidth <= 1024 ? 16 : 24}px` : `60%`, ease: 'power2.out' });
		}
	}, [activeSection, winWidth, router.asPath, quantity, itemsPerPage.current]);

	//! Load more function
	const loadMore = () => {
		setQuantity(quantity + 1);
	};

	return (
		<LatestNewsStyle
			ref={contRef}
			className={className || ''}>
			<Container>
				<Text
					tag={`h1`}
					className={`h4 minion3-medium primary-color9 uppercase`}
					text={title}
				/>

				<Container
					row
					full
					className={`latest-news-content-wrap`}>
					{newsItems}
				</Container>

				{isLoadMore && quantity * itemsPerPage.current < data.length && (
					<div className={`load-more-btn-wrap`}>
						<Button
							btnType={'with-bg-transparent'}
							text={`loadMore`}
							onClick={loadMore}
						/>
					</div>
				)}
			</Container>
		</LatestNewsStyle>
	);
});

export default withUIContext(LatestNews, ['winWidth', 'setEventOrNews', 'eventOrNews']);
